import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Progress } from '../Progress/Progress'

/**
 * Bronson UploadItem component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-upload-item.html
 */
export function UploadItem({
  className,
  error,
  fileName,
  fileSize,
  filesizeProgress,
  fileType,
  hideMainButton,
  hideSecondaryButton,
  icon,
  loading,
  message,
  onMainButtonClick,
  onSecondaryButtonClick,
  progressValue,
  success,
  testId,
  ...otherProps
}) {
  const divClassNameList = classNames(
    'c-upload-item',
    {
      // Convenience props from Bronson variants.
      'is-loading': loading,
      'is-success': success,
      'is-error': error,
    },
    className
  ).trim()

  const iClassNameList = classNames('c-upload-item__icon', 'c-icon', [`c-icon--[${icon}]`]).trim()

  function renderIfUploadItemMessage() {
    if (message) {
      return <span className="c-upload-item__message">{message}</span>
    }
    return null
  }

  function renderIfMain() {
    return hideMainButton ? null : (
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <button
        className="c-upload-item__interaction-icon c-upload-item__interaction-icon--main"
        onClick={onMainButtonClick}
        type="button"
      />
    )
  }

  function renderIfSecondary() {
    return hideSecondaryButton ? null : (
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <button
        className="c-upload-item__interaction-icon c-upload-item__interaction-icon--secondary"
        onClick={onSecondaryButtonClick}
        type="button"
      />
    )
  }

  return (
    <div {...otherProps} data-testid={testId} className={divClassNameList}>
      {icon && <i className={iClassNameList} data-filetype={fileType} aria-hidden="true" role="img" />}
      <div className="c-upload-item__content">
        <div className="c-upload-item__title-row">
          <p className="c-upload-item__title">{fileName}</p>
          <div className="c-upload-item__interaction-icons">
            {renderIfMain()}
            {renderIfSecondary()}
            <i className="c-upload-item__status-icon" aria-hidden="true" role="img" />
          </div>
        </div>
        <div className="c-upload-item__progress">
          <Progress value={progressValue} success={success} error={error} />
        </div>
        <p className="c-upload-item__subtitle-row">
          <span className="c-upload-item__filesize-progress">{filesizeProgress} of </span>
          <span className="c-upload-item__filesize">{fileSize}</span>
          <span className="c-upload-item__filesize-progress"> ({progressValue}% uploaded)</span>
          {renderIfUploadItemMessage()}
        </p>
      </div>
    </div>
  )
}

UploadItem.propTypes = {
  className: PropTypes.string, // Bronson template: 'upload-item-modifier'.
  fileName: PropTypes.string, // Bronson template: 'upload-item-file-name'.
  fileSize: PropTypes.string, // Bronson template: 'upload-item-file-size'.
  filesizeProgress: PropTypes.string, // Bronson template: 'upload-item-filesize-progress'.
  fileType: PropTypes.string, // Bronson template: 'upload-item-file-type'.
  hideMainButton: PropTypes.bool,
  hideSecondaryButton: PropTypes.bool,
  icon: PropTypes.string, // Bronson template: 'upload-item-icon'.
  message: PropTypes.node, // Bronson template: 'upload-item-message'.
  onMainButtonClick: PropTypes.func,
  onSecondaryButtonClick: PropTypes.func,
  progressValue: PropTypes.string, // Bronson template: 'progress-value'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /**
   * Convenience props.
   */
  error: PropTypes.bool, // Convenience prop for `.is-error`. Bronson template: 'upload-item-modifier'.
  loading: PropTypes.bool, // Convenience prop for `.is-loading`. Bronson template: 'upload-item-modifier'.
  success: PropTypes.bool, // Convenience prop for `.is-success`. Bronson template: 'upload-item-modifier'.
}
